import { useMemo, useRef, useState } from 'react';

import { ActionText } from '@components/action-text';
import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { Card } from '@components/card';
import { CodeBlock } from '@components/code-block';
import { createForm, useCreateFormStore } from '@components/form';
import { FormError } from '@components/form-error';
import { HideContainer } from '@components/hide-container';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconIntegration } from '@components/icons/integration';
import { IconList } from '@components/icons/list';
import { IconMappingOff } from '@components/icons/mapping-off';
import { IconRestart } from '@components/icons/restart';
import { IconSend } from '@components/icons/send';
import { IconStatusIndicator } from '@components/icons/status-indicator';
import { IconTextShort } from '@components/icons/text-short';
import { InfoBox } from '@components/info-box';
import { useIntegrationList } from '@components/integrations';
import { List, ListSyncTestRequestType } from '@components/integrations/integrations-types';
import { SizedRelativeSpinner, Spinner } from '@components/loading-spinner';
import { hideModal, Modal, useModal } from '@components/modal';
import { Separator } from '@components/separator';
import { hideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingGroup } from '@components/text-editors/addons/field-mapping';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import { Color } from '@styles/theme';

import { useListSyncTestRequest } from 'api/integrations/hooks';
import { useCreateSyncList, useLists, usePatchSyncList } from 'api/lists/hooks';
import { SyncListCreateData } from 'api/lists/requests';
import { VariableValue } from 'api/types';

export const useSyncListSidebar = () => {
  const sidebar = useSidebar(SyncListEditSidebar, {
    variant: 'REGULAR',
    closeOnClickOutside: true,
  });
  return sidebar;
};

type SyncListFormType = {
  name: string;
  integration: string;
  url: string;
  mappingLabel: VariableValue;
  mappingValue: string;
};

const Form = createForm<SyncListFormType>();

type SyncListEditSidebarProps = {
  list?: List;
};

const SyncListEditSidebar = (props: SyncListEditSidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const { list } = props;
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const integrationsQuery = useIntegrationList();
  const listsQuery = useLists();
  const submitMutation = useCreateSyncList();
  const editSyncListMutation = usePatchSyncList();
  const testRequestModal = useSyncListTestRequestModal();
  const importingModal = useSyncListImportingModal();
  const listName = formStore.useFieldValue('name') as string;
  const integration = formStore.useFieldValue('integration') as string;
  const endpointUrl = formStore.useFieldValue('url') as string;
  const testMutation = useListSyncTestRequest(integration ?? '');
  const [responseStatus, setResponseStatus] = useState<'initial' | 'success' | 'error'>(
    list ? 'success' : 'initial'
  );
  const [testResponse, setTestResponse] = useState<ListSyncTestRequestType | null>(null);
  const isEdit = list && Boolean(list);

  const handleTestEndpoint = async () => {
    changeSource(integration, endpointUrl);
    const response = await testMutation.mutateAsync({ url: endpointUrl });
    setTestResponse(response);
    testRequestModal({ response });
    setResponseStatus(response.success ? 'success' : 'error');
  };
  const mappingKeys = testResponse?.mappingKeys ?? list?.sync?.mappingKeys;
  const options = useMemo(() => {
    return mappingKeys
      ? mappingKeys.map((key) => ({
          label: key,
          value: key,
        }))
      : [];
  }, [mappingKeys]);

  const variableGroups: FieldMappingGroup[] = useMemo(
    () => [
      {
        id: 'list',
        label: 'List',
        fields: options.map((option) => ({
          label: option.label,
          value: option.value,
          name: option.value,
          source: option.value,
          field: 'string',
        })),
        icon: <IconTextShort />,
      },
    ],
    [options]
  );

  const defaultMappingLabel = useMemo(() => {
    // Convert "{{label}}"" to { value:"label_string", variables:{label_string:{source:label, field:string, label:label}}}
    const fields = variableGroups.length > 0 ? variableGroups[0].fields : [];
    const label = list?.sync?.mappingLabel ?? '';
    const value = label.replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (_, key) => `{{${key}_string}}`);
    const variables = Array.from(label.matchAll(/{{\s*(\w+)\s*}}/g)).reduce((acc, [, key]) => {
      const field = fields.find((f) => f.label === key);
      if (field) {
        acc[`${key}_string`] = field;
      }
      return acc;
    }, {} as Record<string, (typeof fields)[number]>);

    return {
      value,
      variables,
    };
  }, [list?.sync?.mappingLabel, variableGroups]);

  const showInitialMappingRef = useRef(false);

  const changeSource = (integrationValue: string, endpointUrlValue: string) => {
    if (
      integrationValue === list?.sync?.integration.shortId &&
      endpointUrlValue === list?.sync?.endpointUrl
    ) {
      setResponseStatus('success');
      showInitialMappingRef.current = false;
    } else {
      setResponseStatus('initial');
      showInitialMappingRef.current = true;
    }
  };

  const handleCreateList = (formData: SyncListFormType) => {
    const data: SyncListCreateData = {
      name: formData.name,
      integrationId: formData.integration,
      endpointUrl: formData.url,
      mappingLabel: formData.mappingLabel.value,
      mappingValue: formData.mappingValue,
      isActive: true,
    };
    if (isEdit) {
      editSyncListMutation.mutateAsync({
        id: list.sync?.shortId ?? '',
        data: data,
      });
    } else {
      submitMutation.mutateAsync(data);
    }
    hideSidebar();
    importingModal();
  };

  if (!integrationsQuery.data || !listsQuery.data) {
    return <Spinner />;
  }
  const listNames = listsQuery.data?.map((l) => l.name);
  const duplicateListName = listNames?.includes(listName) && listName !== list?.name;

  const integrations = integrationsQuery.data;
  const integrationOptions = integrations
    .map(
      (integration) =>
        integration.authentication?.isAuthenticated && {
          label: (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginRight: '8px' }}>
                <IntegrationThumbnail thumbnail={integration.thumbnail} />
              </div>
              {integration.name}
            </div>
          ),
          value: integration.shortId,
          filterText: integration.name,
        }
    )
    .filter((item): item is { label: JSX.Element; value: string; filterText: string } =>
      Boolean(item)
    );

  const responseStatusMap: Record<
    'initial' | 'success' | 'error',
    { Icon: React.ComponentType<{ color?: Color; size?: number }>; text: string; color: Color }
  > = {
    initial: {
      Icon: IconFormIndicatorInfo,
      text: t('testNotPerformed'),
      color: 'elementIndigo100',
    },
    success: { Icon: IconFormIndicatorSuccess, text: t('testSuccess'), color: 'elementGreen100' },
    error: {
      Icon: IconFormIndicatorError,
      text: testResponse?.error ?? t('testError'),
      color: 'elementRed100',
    },
  };

  const { Icon, text, color } = responseStatusMap[responseStatus];

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={IconList}
        iconColor="elementBlue100"
        title={isEdit ? t('editSyncList') : t('addSyncList')}
      />
      <Form store={formStore} onSubmit={handleCreateList}>
        <Sidebar.Content>
          <Stack space={4}>
            <HideContainer title={t('listDetails')}>
              <Stack space={4}>
                <Text text={t('createListDescription')} font="regular14" />
                <InfoBox variant="indigo" icon={<IconFormIndicatorInfo color="indigo100" />}>
                  <Text
                    font="regular14"
                    text={styledTranslate({
                      key: 'infoBoxText',
                      variables: {
                        article: (
                          <ActionText.ExternalLink
                            href={
                              'https://help.journeys.50skills.app/en/articles/227259-synced-lists'
                            }
                            color="textSecondary"
                            font="regular14"
                            underline
                          >
                            {t('article')}
                          </ActionText.ExternalLink>
                        ),
                      },
                      translations: locale,
                    })}
                  />
                </InfoBox>
                <Form.TextField
                  id="name"
                  placeholder={t('addListName')}
                  label={t('listName')}
                  autoFocus
                  validators={['required']}
                  defaultValue={list?.name}
                />
                {duplicateListName && <FormError text={t('duplicateListTitle')} />}
              </Stack>
            </HideContainer>
            <Separator />
            <HideContainer title={t('source')}>
              <Stack space={4}>
                <Text text={t('sourceInfo')} font="regular14" />
                <Form.Select
                  id="integration"
                  isClearable={false}
                  label={t('sourceIntegration')}
                  options={integrationOptions}
                  validators={['required']}
                  onChange={(value) => changeSource(value, endpointUrl)}
                  defaultValue={list?.sync?.integration.shortId}
                  hasFilterText
                />
                <Form.TextField
                  id="url"
                  placeholder={`${t('addUrl')}...`}
                  label={t('sourceUrl')}
                  validators={['required', 'valid-http-url']}
                  onChange={(value) => changeSource(integration, value)}
                  defaultValue={list?.sync?.endpointUrl}
                />
              </Stack>
            </HideContainer>
            <Separator />
            <HideContainer title={t('testResponse')}>
              <Stack space={4}>
                <Text text={t('testResponseInfo')} font="regular14" />
                <Stack space={4} orientation="horizontal">
                  <Button
                    iconLeft={responseStatus === 'success' ? <IconRestart /> : <IconSend />}
                    isDisabled={!integration || !endpointUrl || testMutation.isLoading}
                    text={responseStatus === 'success' ? t('reTest') : t('test')}
                    variant={responseStatus === 'success' ? 'secondary' : 'primary-blue'}
                    onClick={() => handleTestEndpoint()}
                  />
                  {testMutation.isLoading ? (
                    <SizedRelativeSpinner size={24} />
                  ) : (
                    <Stack space={2} orientation="horizontal" align="center">
                      <Icon color={color} size={16} />
                      <Text text={text} font="regular14" color={color} />
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </HideContainer>
            <Separator />
            <HideContainer title={t('mapFields')}>
              {responseStatus !== 'success' ? (
                <Card space={6} variant="gray">
                  <Stack space={4} orientation="horizontal">
                    <IconMappingOff />
                    <Text text={t('mapFieldsInfo')} font="semibold12" />
                  </Stack>
                </Card>
              ) : (
                <Stack space={4}>
                  <Form.FieldMappingTextEditor
                    id="mappingLabel"
                    key={`mapping-label-${responseStatus}`}
                    label={t('label')}
                    singleLine
                    plaintext
                    fieldMappingId={`synced-list-${list?.sync?.shortId}`}
                    variableGroups={variableGroups}
                    data-testid="label-field"
                    validators={['required']}
                    defaultValue={!showInitialMappingRef.current ? defaultMappingLabel : undefined}
                  />
                  <Form.Select
                    id="mappingValue"
                    key={`mapping-value-${responseStatus}`}
                    label={t('value')}
                    options={options ?? []}
                    validators={['required']}
                    defaultValue={
                      !showInitialMappingRef.current ? list?.sync?.mappingValue : undefined
                    }
                  />
                </Stack>
              )}
            </HideContainer>
          </Stack>
        </Sidebar.Content>
        <Sidebar.Footer>
          <Form.Actions>
            <ButtonGroup align="right">
              <Button onClick={hideSidebar} text={t('core.cancel')} variant="secondary" />
              <Form.Submit variant="primary-blue" text={t('save')} isDisabled={!isDirty} />
            </ButtonGroup>
          </Form.Actions>
        </Sidebar.Footer>
      </Form>
    </Sidebar>
  );
};

const useSyncListTestRequestModal = () => useModal(SyncListTestRequest, { variant: 'LARGE' });

type Props = {
  response: ListSyncTestRequestType;
};

const SyncListTestRequest = (props: Props) => {
  const { t } = useScopedTranslate(locale);
  const { exampleData, statusCode, success } = props.response;
  return (
    <Modal>
      <Modal.Header>
        <Text font="heading1" text={t('testResponse')} />
      </Modal.Header>
      <Modal.Content>
        <Stack space={4}>
          <Stack orientation="horizontal" align="center">
            <IconStatusIndicator color={success ? 'elementGreen200' : 'elementRed100'} />
            <Text
              font="regular14"
              color={success ? 'elementGreen200' : 'elementRed100'}
              text={t(success ? 'success' : 'fail', { status: statusCode })}
            />
          </Stack>
          <CodeBlock data={exampleData} hideFooter />
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="flex-end">
          <Button text={t('core.close')} variant="secondary" onClick={hideModal} />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const useSyncListImportingModal = () => useModal(SyncListImporting, { variant: 'LARGE' });

const SyncListImporting = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <Modal>
      <Modal.Header>
        <Text font="heading1" text={t('importing')} />
      </Modal.Header>
      <Modal.Content>
        <Stack space={1}>
          <Text text={t('syncProcessed')} font="bold16" />
          <Text text={t('syncTime')} />
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" justify="flex-end">
          <Button text={t('core.close')} variant="secondary" onClick={hideModal} />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};
export const IntegrationThumbnail = ({ thumbnail }: { thumbnail?: string | null }) => {
  if (thumbnail) {
    return <SquareImage src={thumbnail} />;
  }

  return <IconIntegration color="elementPurpleInvert" />;
};

export const locale = {
  en: {
    addSyncList: 'Add sync list',
    editSyncList: 'Edit sync list',
    listDetails: 'List details',
    createListDescription:
      'Create an automatic sync with list data in your third-party system. Please note you must have an active integration with the system in order to sync lists',
    infoBoxText: 'For more information please look at our help {article}',
    article: 'article',
    listName: 'List name',
    addListName: 'Add list name',
    source: 'Source',
    sourceInfo:
      'Select the third-party integration that you will use as your primary source for this list',
    sourceIntegration: 'Source integration',
    sourceUrl: 'Source URL',
    addUrl: 'Add url',
    testResponse: 'Test response',
    testResponseInfo: 'Checks that the list response is correct.',
    test: 'Test',
    testNotPerformed: 'Test not performed',
    testSuccess: 'Test successful',
    testError: 'Test failed',
    testInitial: 'Test not performed',
    reTest: 'Re-test',
    mapFields: 'Map fields',
    mapFieldsInfo: 'A valid test response has to be received before fields can be mapped',
    label: 'Label',
    value: 'Value',
    save: 'Save',
    duplicateListTitle: 'List with this name already exists',
    success: 'Success • {status}',
    fail: 'Fail • {status}',
    importing: 'Importing',
    syncProcessed: 'Sync is being processed in the background',
    syncTime:
      'Please note this can take some time depending on how many list options are being added',
  },
};
